import React, { useState, useEffect, useRef } from 'react';
import {
  insertPropertyPhoto,
  getPropertyPhotos,
  deletePropertyPhoto,
  setPrimaryPhoto,
} from '../apis/services/pgApi';
import '../scss/components/_addpgphotos.scss';

const MAX_PHOTOS = 15;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];

const PHOTO_TYPE_OPTIONS = [
  { label: 'Single', value: 'SINGLE' },
  { label: 'Double', value: 'DOUBLE' },
  { label: 'Triple', value: 'TRIPLE' },
  { label: 'Common', value: 'PG' },
];

const AddPGPhotosModal = ({ isOpen, onClose, selectedPG, onSuccess }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadStatus, setUploadStatus] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const fileInputRef = useRef(null);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (uploadStatus && !uploading && !error) {
      const timer = setTimeout(() => setUploadStatus(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [uploadStatus, uploading, error]);

  useEffect(() => {
    if (isOpen && selectedPG?.PGID) {
      fetchPhotos();
      setSelectedFiles([]);
      setSelectedPhotoIds([]);
      setUploadProgress({ current: 0, total: 0 });
      setUploadStatus('');
      setError('');
      setConfirmDelete(null);
    }
  }, [isOpen, selectedPG]);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await getPropertyPhotos(selectedPG.PGID);
      setPhotos(response || []);
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  // ─── File handling ──────────────────────────────────────────────
  const handleFiles = (files) => {
    if (files.length === 0) return;
    const currentCount = selectedFiles.length + photos.length;
    const availableSlots = MAX_PHOTOS - currentCount;
    if (files.length > availableSlots) {
      setError(`You can only add ${availableSlots} more photo(s).`);
      return;
    }
    const validFiles = [];
    for (const file of files) {
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        setError(`"${file.name}" is not a supported image format. Use JPG, PNG, or WebP.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`"${file.name}" exceeds 5MB limit.`);
        return;
      }
      const preview = URL.createObjectURL(file);
      const title = file.name.replace(/\.[^/.]+$/, '');
      validFiles.push({ 
        file, 
        title, 
        preview,
        photoType: 'PG' // Default to Common
      });
    }
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setError('');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) handleFiles(files);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
    document.querySelector('.pgp-dropzone')?.classList.remove('pgp-drag-active');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.querySelector('.pgp-dropzone')?.classList.add('pgp-drag-active');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.querySelector('.pgp-dropzone')?.classList.remove('pgp-drag-active');
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleTitleChange = (index, newTitle) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      updated[index].title = newTitle;
      return updated;
    });
  };

  const handlePhotoTypeChange = (index, newType) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      updated[index].photoType = newType;
      return updated;
    });
  };

  // ─── Upload ──────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one photo');
      return;
    }

    setUploading(true);
    setUploadStatus(`Uploading 1/${selectedFiles.length}...`);
    setError('');
    let successCount = 0;
    let failCount = 0;
    const newPhotos = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const { file, title, photoType } = selectedFiles[i];
      setUploadProgress({ current: i + 1, total: selectedFiles.length });
      setUploadStatus(`Uploading ${i + 1}/${selectedFiles.length}...`);

      const formData = new FormData();
      formData.append('photo', file);

      const nextOrder = photos.length + i + 1;
      const insertDto = {
        PGID: selectedPG.PGID,
        PhotoType: photoType || 'PG', // Use selected photo type
        PhotoTitle: title || `PG Photo ${nextOrder}`,
        DisplayOrder: nextOrder,
        IsPrimary: (photos.length === 0 && i === 0) ? 1 : 0,
        Status: 'ACTIVE',
      };

      formData.append('insertDto', JSON.stringify(insertDto));

      try {
        const response = await insertPropertyPhoto(formData);
        if (response.PhotoID) {
          successCount++;
          newPhotos.push({
            PhotoID: response.PhotoID,
            PhotoTitle: insertDto.PhotoTitle,
            PhotoType: insertDto.PhotoType,
            DisplayOrder: insertDto.DisplayOrder,
            IsPrimary: insertDto.IsPrimary,
            PhotoURL: response.PhotoURL || URL.createObjectURL(file),
          });
        } else {
          failCount++;
        }
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        failCount++;
      }
    }

    // Update state with new photos
    setPhotos(prev => {
      const hasPrimary = newPhotos.some(p => p.IsPrimary === 1);
      const updated = [...prev];
      if (hasPrimary) updated.forEach(p => p.IsPrimary = 0);
      return [...updated, ...newPhotos];
    });

    if (failCount === 0 && successCount > 0) {
      setError('');
      setUploadStatus(`✅ ${successCount} photo(s) uploaded successfully!`);
      if (onSuccess) onSuccess();
    } else if (successCount > 0 && failCount > 0) {
      setError(`⚠️ ${successCount} uploaded, ${failCount} failed. Check console for details.`);
    } else {
      setError('❌ All uploads failed. Please try again.');
    }

    selectedFiles.forEach(({ preview }) => URL.revokeObjectURL(preview));
    setSelectedFiles([]);
    setSelectedPhotoIds([]);
    setUploadProgress({ current: 0, total: 0 });
    setUploading(false);
  };

  // ─── Gallery Actions ─────────────────────────────────────────────
  const handleSetPrimary = async (photoId) => {
    try {
      await setPrimaryPhoto(photoId);
      setPhotos(prev =>
        prev.map(p => ({
          ...p,
          IsPrimary: p.PhotoID === photoId ? 1 : 0,
        }))
      );
    } catch (err) {
      console.error('Error setting primary photo:', err);
      setError('Failed to set primary photo');
    }
  };

  const handleDeleteSingle = async (photoId) => {
    try {
      await deletePropertyPhoto(photoId);
      setPhotos(prev => prev.filter(p => p.PhotoID !== photoId));
      setSelectedPhotoIds(prev => prev.filter(id => id !== photoId));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotoIds.length === 0) return;
    setConfirmDelete({
      photoIds: selectedPhotoIds,
      count: selectedPhotoIds.length,
      title: `${selectedPhotoIds.length} photo(s)`,
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = confirmDelete.photoIds;
    try {
      await Promise.all(ids.map(id => deletePropertyPhoto(id)));
      setPhotos(prev => prev.filter(p => !ids.includes(p.PhotoID)));
      setSelectedPhotoIds([]);
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error during bulk delete:', err);
      setError('Some photos could not be deleted. Please try again.');
      setConfirmDelete(null);
    }
  };

  // ─── Selection helpers ───────────────────────────────────────────
  const toggleSelectAll = () => {
    if (selectedPhotoIds.length === photos.length) {
      setSelectedPhotoIds([]);
    } else {
      setSelectedPhotoIds(photos.map(p => p.PhotoID));
    }
  };

  const toggleSelect = (photoId) => {
    setSelectedPhotoIds(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const isAllSelected = photos.length > 0 && selectedPhotoIds.length === photos.length;

  if (!isOpen) return null;

  const canUploadMore = photos.length + selectedFiles.length < MAX_PHOTOS;

  // Get photo type label
  const getPhotoTypeLabel = (type) => {
    const option = PHOTO_TYPE_OPTIONS.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  return (
    <div className="pgp-modal-overlay">
      <div className="pgp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pgp-modal-header">
          <h3>PG Photos - {selectedPG?.PGName}</h3>
          <button className="pgp-modal-close" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="pgp-modal-body">
          {/* ─── Upload Section ────────────────────────────────── */}
          <div className="pgp-upload-section">
            <h4>
              Upload Photos
              <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px' }}>
                ({photos.length + selectedFiles.length}/{MAX_PHOTOS})
              </span>
            </h4>

            {/* Dropzone */}
            {canUploadMore ? (
              <div
                className="pgp-dropzone"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDragEnd={() => document.querySelector('.pgp-dropzone')?.classList.remove('pgp-drag-active')}
                style={{ cursor: 'default' }}
              >
                <div className="pgp-dropzone-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
                <div className="pgp-dropzone-text">Drop photos here</div>
                <div className="pgp-dropzone-hint">or use the button below • JPG, PNG, WebP • max 5MB each</div>
                {selectedFiles.length > 0 && (
                  <div className="pgp-dropzone-files">
                    {selectedFiles.length} file(s) selected
                  </div>
                )}
              </div>
            ) : (
              <div className="pgp-dropzone" style={{ borderColor: '#d1d5db', background: '#f3f4f6', cursor: 'default' }}>
                <div className="pgp-dropzone-text" style={{ color: '#6b7280' }}>Maximum photos reached</div>
                <div className="pgp-dropzone-hint">Remove some photos to add more</div>
              </div>
            )}

            {/* Browse Button – centered */}
            {canUploadMore && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                <button
                  className="pgp-browse-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                  </svg>
                  Browse Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {/* Preview Grid with Title & PhotoType */}
            {selectedFiles.length > 0 && (
              <>
                <div className="pgp-preview-grid">
                  {selectedFiles.map((item, index) => (
                    <div key={index} className="pgp-preview-item">
                      <div className="pgp-preview-thumb">
                        <img src={item.preview} alt={item.title || 'Preview'} />
                      </div>
                      <input
                        type="text"
                        className="pgp-preview-title-input"
                        value={item.title}
                        onChange={(e) => handleTitleChange(index, e.target.value)}
                        placeholder="Enter title"
                        disabled={uploading}
                      />
                      <div className="pgp-preview-type-wrapper">
                        <select
                          className="pgp-preview-type-select"
                          value={item.photoType || 'PG'}
                          onChange={(e) => handlePhotoTypeChange(index, e.target.value)}
                          disabled={uploading}
                        >
                          {PHOTO_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="pgp-preview-file-size">
                        {(item.file.size / 1024).toFixed(0)} KB
                      </div>
                      <button
                        className="pgp-preview-remove"
                        onClick={() => handleRemoveFile(index)}
                        disabled={uploading}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <div className="pgp-upload-actions">
                  <button
                    className="pgp-upload-btn"
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0 || uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="pgp-spinner"></span>
                        Uploading {uploadProgress.current}/{uploadProgress.total}...
                      </>
                    ) : (
                      `Upload ${selectedFiles.length} Photo(s)`
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ─── Gallery Section ────────────────────────────────── */}
          {/* Only render if there are photos or we're loading */}
          {(photos.length > 0 || loading) && (
            <div className="pgp-gallery-section">
              {loading ? (
                <div className="pgp-loading">Loading photos...</div>
              ) : (
                <>
                  <div className="pgp-gallery-toolbar">
                    <div className="pgp-toolbar-left">
                      <label className="pgp-select-all">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={toggleSelectAll}
                        />
                        Select All
                      </label>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        {selectedPhotoIds.length} selected
                      </span>
                    </div>
                    <div className="pgp-toolbar-right">
                      <button
                        className="pgp-delete-selected-btn"
                        onClick={handleBulkDelete}
                        disabled={selectedPhotoIds.length === 0}
                      >
                        Delete Selected ({selectedPhotoIds.length})
                      </button>
                    </div>
                  </div>

                  <div className="pgp-photos-grid">
                    {photos.map((photo) => (
                      <div
                        key={photo.PhotoID}
                        className={`pgp-photo-card ${selectedPhotoIds.includes(photo.PhotoID) ? 'pgp-selected' : ''}`}
                      >
                        <div className="pgp-photo-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedPhotoIds.includes(photo.PhotoID)}
                            onChange={() => toggleSelect(photo.PhotoID)}
                          />
                        </div>
                        <div className="pgp-photo-image">
                          <img src={photo.PhotoURL} alt={photo.PhotoTitle} />
                          <span className="pgp-photo-type-badge">
                            {getPhotoTypeLabel(photo.PhotoType || 'PG')}
                          </span>
                          {photo.IsPrimary === 1 && (
                            <span className="pgp-primary-badge">★ Primary</span>
                          )}
                        </div>
                        <div className="pgp-photo-info">
                          <div className="pgp-photo-title">{photo.PhotoTitle}</div>
                          <div className="pgp-photo-order">Order: {photo.DisplayOrder}</div>
                          <div className="pgp-photo-actions">
                            {photo.IsPrimary !== 1 && (
                              <button
                                className="pgp-action-btn pgp-primary-btn"
                                onClick={() => handleSetPrimary(photo.PhotoID)}
                                title="Set as Primary"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M12 2L15 8.5L22 9.5L17 14L18.5 21L12 17.5L5.5 21L7 14L2 9.5L9 8.5L12 2Z" />
                                </svg>
                              </button>
                            )}
                            <button
                              className="pgp-action-btn pgp-delete-btn"
                              onClick={() => setConfirmDelete({ photoIds: [photo.PhotoID], count: 1, title: photo.PhotoTitle })}
                              title="Delete"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {error && <div className="pgp-error">{error}</div>}
        {uploadStatus && !error && <div className="pgp-success">{uploadStatus}</div>}

        <div className="pgp-modal-footer">
          <button className="pgp-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {/* ─── Custom Confirmation Dialog ────────────────────────── */}
      {confirmDelete && (
        <div className="pgp-confirm-overlay">
          <div className="pgp-confirm-dialog">
            <h4>Delete Photo{confirmDelete.count > 1 ? 's' : ''}</h4>
            <p>
              {confirmDelete.count === 1
                ? `Are you sure you want to delete "${confirmDelete.title}"?`
                : `Are you sure you want to delete ${confirmDelete.count} selected photo(s)?`}
              {confirmDelete.count === 1 && ' This action cannot be undone.'}
            </p>
            <div className="pgp-confirm-actions">
              <button className="pgp-confirm-cancel" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button
                className="pgp-confirm-danger"
                onClick={() => {
                  if (confirmDelete.count === 1) {
                    handleDeleteSingle(confirmDelete.photoIds[0]);
                  } else {
                    handleConfirmBulkDelete();
                  }
                }}
              >
                Delete {confirmDelete.count > 1 ? 'All' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPGPhotosModal;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//   insertPropertyPhoto,
//   getPropertyPhotos,
//   deletePropertyPhoto,
//   setPrimaryPhoto,
// } from '../apis/services/pgApi';
// import '../scss/components/_addpgphotos.scss';

// const MAX_PHOTOS = 15;
// const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
// const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];

// const AddPGPhotosModal = ({ isOpen, onClose, selectedPG, onSuccess }) => {
//   const [photos, setPhotos] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [uploading, setUploading] = useState(false);
//   const [error, setError] = useState('');
//   const [selectedFiles, setSelectedFiles] = useState([]);
//   const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
//   const [uploadStatus, setUploadStatus] = useState('');
//   const [confirmDelete, setConfirmDelete] = useState(null);
//   const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
//   const fileInputRef = useRef(null);

//   // Auto-dismiss success message after 4 seconds
//   useEffect(() => {
//     if (uploadStatus && !uploading && !error) {
//       const timer = setTimeout(() => setUploadStatus(''), 4000);
//       return () => clearTimeout(timer);
//     }
//   }, [uploadStatus, uploading, error]);

//   useEffect(() => {
//     if (isOpen && selectedPG?.PGID) {
//       fetchPhotos();
//       setSelectedFiles([]);
//       setSelectedPhotoIds([]);
//       setUploadProgress({ current: 0, total: 0 });
//       setUploadStatus('');
//       setError('');
//       setConfirmDelete(null);
//     }
//   }, [isOpen, selectedPG]);

//   const fetchPhotos = async () => {
//     setLoading(true);
//     try {
//       const response = await getPropertyPhotos(selectedPG.PGID);
//       setPhotos(response || []);
//     } catch (err) {
//       console.error('Error fetching photos:', err);
//       setError('Failed to load photos');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── File handling ──────────────────────────────────────────────
//   const handleFiles = (files) => {
//     if (files.length === 0) return;
//     const currentCount = selectedFiles.length + photos.length;
//     const availableSlots = MAX_PHOTOS - currentCount;
//     if (files.length > availableSlots) {
//       setError(`You can only add ${availableSlots} more photo(s).`);
//       return;
//     }
//     const validFiles = [];
//     for (const file of files) {
//       if (!SUPPORTED_FORMATS.includes(file.type)) {
//         setError(`"${file.name}" is not a supported image format. Use JPG, PNG, or WebP.`);
//         return;
//       }
//       if (file.size > MAX_FILE_SIZE) {
//         setError(`"${file.name}" exceeds 5MB limit.`);
//         return;
//       }
//       const preview = URL.createObjectURL(file);
//       const title = file.name.replace(/\.[^/.]+$/, '');
//       validFiles.push({ file, title, preview });
//     }
//     setSelectedFiles(prev => [...prev, ...validFiles]);
//     setError('');
//   };

//   const handleFileSelect = (e) => {
//     const files = Array.from(e.target.files);
//     if (files.length > 0) handleFiles(files);
//     e.target.value = null;
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     const files = Array.from(e.dataTransfer.files);
//     handleFiles(files);
//     document.querySelector('.pgp-dropzone')?.classList.remove('pgp-drag-active');
//   };

//   const handleDragOver = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     document.querySelector('.pgp-dropzone')?.classList.add('pgp-drag-active');
//   };

//   const handleDragLeave = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     document.querySelector('.pgp-dropzone')?.classList.remove('pgp-drag-active');
//   };

//   const handleRemoveFile = (index) => {
//     setSelectedFiles(prev => {
//       const updated = [...prev];
//       URL.revokeObjectURL(updated[index].preview);
//       updated.splice(index, 1);
//       return updated;
//     });
//   };

//   const handleTitleChange = (index, newTitle) => {
//     setSelectedFiles(prev => {
//       const updated = [...prev];
//       updated[index].title = newTitle;
//       return updated;
//     });
//   };

//   // ─── Upload ──────────────────────────────────────────────────────
//   const handleUpload = async () => {
//     if (selectedFiles.length === 0) {
//       setError('Please select at least one photo');
//       return;
//     }

//     setUploading(true);
//     setUploadStatus(`Uploading 1/${selectedFiles.length}...`);
//     setError('');
//     let successCount = 0;
//     let failCount = 0;
//     const newPhotos = [];

//     for (let i = 0; i < selectedFiles.length; i++) {
//       const { file, title } = selectedFiles[i];
//       setUploadProgress({ current: i + 1, total: selectedFiles.length });
//       setUploadStatus(`Uploading ${i + 1}/${selectedFiles.length}...`);

//       const formData = new FormData();
//       formData.append('photo', file);

//       const nextOrder = photos.length + i + 1;
//       const insertDto = {
//         PGID: selectedPG.PGID,
//         PhotoType: 'PG',
//         PhotoTitle: title || `PG Photo ${nextOrder}`,
//         DisplayOrder: nextOrder,
//         IsPrimary: (photos.length === 0 && i === 0) ? 1 : 0,
//         Status: 'ACTIVE',
//       };

//       formData.append('insertDto', JSON.stringify(insertDto));

//       try {
//         const response = await insertPropertyPhoto(formData);
//         if (response.PhotoID) {
//           successCount++;
//           newPhotos.push({
//             PhotoID: response.PhotoID,
//             PhotoTitle: insertDto.PhotoTitle,
//             DisplayOrder: insertDto.DisplayOrder,
//             IsPrimary: insertDto.IsPrimary,
//             PhotoURL: response.PhotoURL || URL.createObjectURL(file),
//           });
//         } else {
//           failCount++;
//         }
//       } catch (err) {
//         console.error(`Error uploading ${file.name}:`, err);
//         failCount++;
//       }
//     }

//     // Update state with new photos
//     setPhotos(prev => {
//       const hasPrimary = newPhotos.some(p => p.IsPrimary === 1);
//       const updated = [...prev];
//       if (hasPrimary) updated.forEach(p => p.IsPrimary = 0);
//       return [...updated, ...newPhotos];
//     });

//     if (failCount === 0 && successCount > 0) {
//       setError('');
//       setUploadStatus(`✅ ${successCount} photo(s) uploaded successfully!`);
//       if (onSuccess) onSuccess();
//     } else if (successCount > 0 && failCount > 0) {
//       setError(`⚠️ ${successCount} uploaded, ${failCount} failed. Check console for details.`);
//     } else {
//       setError('❌ All uploads failed. Please try again.');
//     }

//     selectedFiles.forEach(({ preview }) => URL.revokeObjectURL(preview));
//     setSelectedFiles([]);
//     setSelectedPhotoIds([]);
//     setUploadProgress({ current: 0, total: 0 });
//     setUploading(false);
//   };

//   // ─── Gallery Actions ─────────────────────────────────────────────
//   const handleSetPrimary = async (photoId) => {
//     try {
//       await setPrimaryPhoto(photoId);
//       setPhotos(prev =>
//         prev.map(p => ({
//           ...p,
//           IsPrimary: p.PhotoID === photoId ? 1 : 0,
//         }))
//       );
//     } catch (err) {
//       console.error('Error setting primary photo:', err);
//       setError('Failed to set primary photo');
//     }
//   };

//   const handleDeleteSingle = async (photoId) => {
//     try {
//       await deletePropertyPhoto(photoId);
//       setPhotos(prev => prev.filter(p => p.PhotoID !== photoId));
//       setSelectedPhotoIds(prev => prev.filter(id => id !== photoId));
//       setConfirmDelete(null);
//     } catch (err) {
//       console.error('Error deleting photo:', err);
//       setError('Failed to delete photo');
//     }
//   };

//   const handleBulkDelete = async () => {
//     if (selectedPhotoIds.length === 0) return;
//     setConfirmDelete({
//       photoIds: selectedPhotoIds,
//       count: selectedPhotoIds.length,
//       title: `${selectedPhotoIds.length} photo(s)`,
//     });
//   };

//   const handleConfirmBulkDelete = async () => {
//     const ids = confirmDelete.photoIds;
//     try {
//       await Promise.all(ids.map(id => deletePropertyPhoto(id)));
//       setPhotos(prev => prev.filter(p => !ids.includes(p.PhotoID)));
//       setSelectedPhotoIds([]);
//       setConfirmDelete(null);
//     } catch (err) {
//       console.error('Error during bulk delete:', err);
//       setError('Some photos could not be deleted. Please try again.');
//       setConfirmDelete(null);
//     }
//   };

//   // ─── Selection helpers ───────────────────────────────────────────
//   const toggleSelectAll = () => {
//     if (selectedPhotoIds.length === photos.length) {
//       setSelectedPhotoIds([]);
//     } else {
//       setSelectedPhotoIds(photos.map(p => p.PhotoID));
//     }
//   };

//   const toggleSelect = (photoId) => {
//     setSelectedPhotoIds(prev =>
//       prev.includes(photoId)
//         ? prev.filter(id => id !== photoId)
//         : [...prev, photoId]
//     );
//   };

//   const isAllSelected = photos.length > 0 && selectedPhotoIds.length === photos.length;

//   if (!isOpen) return null;

//   const canUploadMore = photos.length + selectedFiles.length < MAX_PHOTOS;

//   return (
//     <div className="pgp-modal-overlay">
//       <div className="pgp-modal" onClick={(e) => e.stopPropagation()}>
//         <div className="pgp-modal-header">
//           <h3>PG Photos - {selectedPG?.PGName}</h3>
//           <button className="pgp-modal-close" onClick={onClose}>
//             <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
//               <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
//             </svg>
//           </button>
//         </div>

//         <div className="pgp-modal-body">
//           {/* ─── Upload Section ────────────────────────────────── */}
//           <div className="pgp-upload-section">
//             <h4>
//               Upload Photos
//               <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px' }}>
//                 ({photos.length + selectedFiles.length}/{MAX_PHOTOS})
//               </span>
//             </h4>

//             {/* Dropzone */}
//             {canUploadMore ? (
//               <div
//                 className="pgp-dropzone"
//                 onDragOver={handleDragOver}
//                 onDragLeave={handleDragLeave}
//                 onDrop={handleDrop}
//                 onDragEnd={() => document.querySelector('.pgp-dropzone')?.classList.remove('pgp-drag-active')}
//                 style={{ cursor: 'default' }}
//               >
//                 <div className="pgp-dropzone-icon">
//                   <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
//                     <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
//                     <circle cx="8.5" cy="8.5" r="1.5" />
//                     <polyline points="21 15 16 10 5 21" />
//                   </svg>
//                 </div>
//                 <div className="pgp-dropzone-text">Drop photos here</div>
//                 <div className="pgp-dropzone-hint">or use the button below • JPG, PNG, WebP • max 5MB each</div>
//                 {selectedFiles.length > 0 && (
//                   <div className="pgp-dropzone-files">
//                     {selectedFiles.length} file(s) selected
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div className="pgp-dropzone" style={{ borderColor: '#d1d5db', background: '#f3f4f6', cursor: 'default' }}>
//                 <div className="pgp-dropzone-text" style={{ color: '#6b7280' }}>Maximum photos reached</div>
//                 <div className="pgp-dropzone-hint">Remove some photos to add more</div>
//               </div>
//             )}

//             {/* Browse Button – centered */}
//             {canUploadMore && (
//               <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
//                 <button
//                   className="pgp-browse-btn"
//                   onClick={() => fileInputRef.current?.click()}
//                   disabled={uploading}
//                 >
//                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                     <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
//                   </svg>
//                   Browse Files
//                 </button>
//                 <input
//                   ref={fileInputRef}
//                   type="file"
//                   accept="image/jpeg,image/png,image/webp"
//                   multiple
//                   onChange={handleFileSelect}
//                   style={{ display: 'none' }}
//                 />
//               </div>
//             )}

//             {/* Preview Grid & Upload Button */}
//             {selectedFiles.length > 0 && (
//               <>
//                 <div className="pgp-preview-grid">
//                   {selectedFiles.map((item, index) => (
//                     <div key={index} className="pgp-preview-item">
//                       <div className="pgp-preview-thumb">
//                         <img src={item.preview} alt={item.title || 'Preview'} />
//                       </div>
//                       <input
//                         type="text"
//                         className="pgp-preview-title-input"
//                         value={item.title}
//                         onChange={(e) => handleTitleChange(index, e.target.value)}
//                         placeholder="Enter title"
//                         disabled={uploading}
//                       />
//                       <div className="pgp-preview-file-size">
//                         {(item.file.size / 1024).toFixed(0)} KB
//                       </div>
//                       <button
//                         className="pgp-preview-remove"
//                         onClick={() => handleRemoveFile(index)}
//                         disabled={uploading}
//                         title="Remove"
//                       >
//                         ×
//                       </button>
//                     </div>
//                   ))}
//                 </div>

//                 <div className="pgp-upload-actions">
//                   <button
//                     className="pgp-upload-btn"
//                     onClick={handleUpload}
//                     disabled={selectedFiles.length === 0 || uploading}
//                   >
//                     {uploading ? (
//                       <>
//                         <span className="pgp-spinner"></span>
//                         Uploading {uploadProgress.current}/{uploadProgress.total}...
//                       </>
//                     ) : (
//                       `Upload ${selectedFiles.length} Photo(s)`
//                     )}
//                   </button>
//                 </div>
//               </>
//             )}
//           </div>

//           {/* ─── Gallery Section ────────────────────────────────── */}
//           {/* Only render if there are photos or we're loading */}
//           {(photos.length > 0 || loading) && (
//             <div className="pgp-gallery-section">
//               {loading ? (
//                 <div className="pgp-loading">Loading photos...</div>
//               ) : (
//                 <>
//                   <div className="pgp-gallery-toolbar">
//                     <div className="pgp-toolbar-left">
//                       <label className="pgp-select-all">
//                         <input
//                           type="checkbox"
//                           checked={isAllSelected}
//                           onChange={toggleSelectAll}
//                         />
//                         Select All
//                       </label>
//                       <span style={{ fontSize: '13px', color: '#6b7280' }}>
//                         {selectedPhotoIds.length} selected
//                       </span>
//                     </div>
//                     <div className="pgp-toolbar-right">
//                       <button
//                         className="pgp-delete-selected-btn"
//                         onClick={handleBulkDelete}
//                         disabled={selectedPhotoIds.length === 0}
//                       >
//                         Delete Selected ({selectedPhotoIds.length})
//                       </button>
//                     </div>
//                   </div>

//                   <div className="pgp-photos-grid">
//                     {photos.map((photo) => (
//                       <div
//                         key={photo.PhotoID}
//                         className={`pgp-photo-card ${selectedPhotoIds.includes(photo.PhotoID) ? 'pgp-selected' : ''}`}
//                       >
//                         <div className="pgp-photo-checkbox">
//                           <input
//                             type="checkbox"
//                             checked={selectedPhotoIds.includes(photo.PhotoID)}
//                             onChange={() => toggleSelect(photo.PhotoID)}
//                           />
//                         </div>
//                         <div className="pgp-photo-image">
//                           <img src={photo.PhotoURL} alt={photo.PhotoTitle} />
//                           {/* {photo.IsPrimary === 1 && (
//                             <span className="pgp-primary-badge">Primary</span>
//                           )} */}
//                         </div>
//                         <div className="pgp-photo-info">
//                           <div className="pgp-photo-title">{photo.PhotoTitle}</div>
//                           <div className="pgp-photo-order">Order: {photo.DisplayOrder}</div>
//                           <div className="pgp-photo-actions">
//                             {/* {photo.IsPrimary !== 1 && (
//                               <button
//                                 className="pgp-action-btn pgp-primary-btn"
//                                 onClick={() => handleSetPrimary(photo.PhotoID)}
//                                 title="Set as Primary"
//                               >
//                                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                                   <path d="M12 2L15 8.5L22 9.5L17 14L18.5 21L12 17.5L5.5 21L7 14L2 9.5L9 8.5L12 2Z" />
//                                 </svg>
//                               </button>
//                             )} */}
//                             <button
//                               className="pgp-action-btn pgp-delete-btn"
//                               onClick={() => setConfirmDelete({ photoIds: [photo.PhotoID], count: 1, title: photo.PhotoTitle })}
//                               title="Delete"
//                             >
//                               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                                 <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
//                               </svg>
//                             </button>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
//         </div>

//         {error && <div className="pgp-error">{error}</div>}

//         <div className="pgp-modal-footer">
//           <button className="pgp-close-btn" onClick={onClose}>
//             Close
//           </button>
//         </div>
//       </div>

//       {/* ─── Custom Confirmation Dialog ────────────────────────── */}
//       {confirmDelete && (
//         <div className="pgp-confirm-overlay">
//           <div className="pgp-confirm-dialog">
//             <h4>Delete Photo{confirmDelete.count > 1 ? 's' : ''}</h4>
//             <p>
//               {confirmDelete.count === 1
//                 ? `Are you sure you want to delete "${confirmDelete.title}"?`
//                 : `Are you sure you want to delete ${confirmDelete.count} selected photo(s)?`}
//               {confirmDelete.count === 1 && ' This action cannot be undone.'}
//             </p>
//             <div className="pgp-confirm-actions">
//               <button className="pgp-confirm-cancel" onClick={() => setConfirmDelete(null)}>
//                 Cancel
//               </button>
//               <button
//                 className="pgp-confirm-danger"
//                 onClick={() => {
//                   if (confirmDelete.count === 1) {
//                     handleDeleteSingle(confirmDelete.photoIds[0]);
//                   } else {
//                     handleConfirmBulkDelete();
//                   }
//                 }}
//               >
//                 Delete {confirmDelete.count > 1 ? 'All' : ''}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AddPGPhotosModal;